import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { Accordion, Button, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { DOCS_URLS } from '../../constants';
import { OnboardingChecklist, OnboardingChecklistItem } from './onboarding-checklist';
import { onboardingStepBadge } from './onboarding-step-badge';
import { OnboardingStepIcon } from './onboarding-step-icon';

const COMPONENT = 'features:home.components.onboarding.launch' satisfies I18nPrefix;

export function LaunchStep({
  isExpanded,
  onExpandedChange,
  loading,
  onToggle,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  loading: boolean;
  onToggle: () => void;
}) {
  const env = useEnv();
  const complete = env.onboarding_launch;

  const [familiarWithInvites, setFamiliarWithInvites] = useState<boolean>(false);
  const [invitedCustomers, setInvitedCustomers] = useState<boolean>(false);
  const [launched, setLaunched] = useState<boolean>(false);
  const allChecked = familiarWithInvites && invitedCustomers && launched;

  // Unchecking the step resets its checklist so the user re-acknowledges each item.
  useEffect(() => {
    if (!complete) {
      setFamiliarWithInvites(false);
      setInvitedCustomers(false);
      setLaunched(false);
    }
  }, [complete]);

  return (
    <Accordion.Item
      title={t(`${COMPONENT}.title`)}
      subtitle={t(complete ? `${COMPONENT}.subtitle_complete` : `${COMPONENT}.subtitle_incomplete`)}
      icon={<OnboardingStepIcon complete={complete} />}
      badge={onboardingStepBadge(complete)}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      <Text variant="secondary">{t(`${COMPONENT}.description`)}</Text>

      <OnboardingChecklist>
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.familiar_with_invites.label`)}
          description={t(`${COMPONENT}.checklist.familiar_with_invites.description`)}
          learnMoreLink={DOCS_URLS.inviteCustomers}
          checked={familiarWithInvites}
          onChange={() => setFamiliarWithInvites((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.invited_customers.label`)}
          description={t(`${COMPONENT}.checklist.invited_customers.description`)}
          checked={invitedCustomers}
          onChange={() => setInvitedCustomers((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.launched.label`)}
          description={t(`${COMPONENT}.checklist.launched.description`)}
          checked={launched}
          onChange={() => setLaunched((prev) => !prev)}
        />
      </OnboardingChecklist>

      <Text variant="secondary" size="sm">
        {t(`${COMPONENT}.checklist_hint`)} <LearnMoreLink link={DOCS_URLS.onboardingChecklist} />
      </Text>

      <div>
        <Button
          variant={complete ? 'secondary' : 'primary'}
          disabled={!complete && !allChecked}
          loading={loading}
          onClick={onToggle}
        >
          {t(complete ? `${COMPONENT}.action_undo` : `${COMPONENT}.action`)}
        </Button>
      </div>
    </Accordion.Item>
  );
}
